
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useDocumentDownload = () => {
  const { toast } = useToast();

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const handleDownload = async (filename: string, title: string) => {
    try {
      console.log('Attempting to download:', filename);
      console.log('Device is iOS:', isIOS());
      
      // Convert filename to proper format (I-, N-, or G- prefix)
      const formattedFilename = filename
        .replace(/^i-/i, 'I-')
        .replace(/^n-/i, 'N-')
        .replace(/^g-/i, 'G-')
        .toUpperCase()
        // Remove any language suffixes
        .replace(/-[a-z]{2}\.pdf$/i, '.pdf');
      
      console.log('Formatted filename:', formattedFilename);

      // Search for files with the formatted filename
      const { data: files } = await supabase
        .storage
        .from('documents')
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' }
        });

      console.log('Available files in storage:', files);

      // Find the matching file (case-insensitive)
      const matchingFile = files?.find(file => 
        file.name.toUpperCase() === formattedFilename
      );

      if (!matchingFile) {
        console.log('No matching file found for:', formattedFilename);
        throw new Error('File not found in storage');
      }

      console.log('Found matching file:', matchingFile.name);

      // Create signed URL using the exact filename from storage
      const { data, error } = await supabase
        .storage
        .from('documents')
        .createSignedUrl(matchingFile.name, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        throw error;
      }

      if (!data?.signedUrl) {
        throw new Error('No signed URL received');
      }

      console.log('Successfully generated signed URL');

      if (isIOS()) {
        // For iOS, open in a new tab and show a toast message
        window.open(data.signedUrl, '_blank');
        toast({
          title: "Documento listo",
          description: `El documento "${title}" se abrirá en una nueva pestaña. Si no se abre automáticamente, verifique el bloqueador de ventanas emergentes.`,
          duration: 5000,
        });
      } else {
        // For other devices, use the download attribute
        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.setAttribute('download', matchingFile.name);
        document.body.appendChild(link);
        link.click();
        
        // Small delay before removing the link
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        toast({
          title: "Descarga iniciada",
          description: `El documento "${title}" se está descargando.`,
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      
      let errorMessage = `No se pudo descargar el documento "${title}".`;
      if (error.message === 'File not found in storage') {
        errorMessage += ' El archivo no se encuentra en el sistema.';
      } else {
        errorMessage += ' Por favor contacte al administrador.';
      }

      toast({
        title: "Error de descarga",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return { handleDownload };
};
